import { useMemo, useState } from "react";
import { Pencil, Search, Trash, X } from "lucide-react";
import type { KeyValueBase, KeyValueItem } from "@repo/types/ui/keyValuePairs";
import KeyValueMenuItem from "./KeyValueMenuItem";
import Button from "../button/Button";
import type { RecordId } from "@repo/types/ui/tables";
import Input from "../../form/input/InputField";

export interface KeyValueMenuViewProps extends KeyValueBase {
  items: KeyValueItem[] | undefined;
  onValueChange: (newValue: number) => Promise<void>;
  selectedKeys: RecordId[];
  customActionTriggerRenderer?: (onClick: () => void) => React.ReactNode;
  editText?: string;
  onCustomActionClick?: () => void;
  mode?: "default" | "assignee";
}

export default function KeyValueMenuView({
  items,
  onValueChange,
  selectedKeys,
  customActionTriggerRenderer,
  itemsEditable,
  itemsDeletable,
  editText,
  onEditClick,
  onDeleteClick,
  onCustomActionClick,
  maxHeight,
  mode = "default",
}: KeyValueMenuViewProps) {
  const [search, setSearch] = useState("");

  // Optimized filtering
  const filteredItems = useMemo(() => {
    return (items || []).filter((item) =>
      item.Value?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [items, search]);

  const selectedItems = useMemo(() => {
    return (items || []).filter((i) => selectedKeys.includes(i.Id));
  }, [items, selectedKeys]);

  return (
    <>
      <div
        className={`${items && items.length === 0 ? "min-h-50" : ""} p-2 space-y-2`}
      >
        {/* SELECTED USERS */}
        {mode === "assignee" && selectedItems.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedItems.map((item) => (
              <div
                key={item.Id}
                className="flex items-center gap-2 bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs"
              >
                <KeyValueMenuItem item={item} variant="chip" />

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onValueChange(item.Id);
                  }}
                  className="text-blue-600 hover:text-red-500 cursor-pointer"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* SEARCH */}
        {mode === "assignee" && (
          <div className="flex items-center rounded bg-white relative">
            <Input
              type="text"
              placeholder="Search names, roles or teams"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full outline-none text-sm pl-8 h-7.5"
            />

            <button
              onClick={() => search && setSearch("")}
              className="absolute left-2 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              {search ? <X size={16} /> : <Search size={16} />}
            </button>
          </div>
        )}

        {mode === "assignee" && (
          <div>
            <p className="text-gray-500 text-sm">Suggested people</p>
          </div>
        )}

        {/* LIST */}
        {items && items.length > 0 && (
          <div
            className="overflow-y-auto w-full"
            style={{ maxHeight: maxHeight ?? "200px" }}
          >
            {filteredItems.map((item) => {
              const isSelected = selectedKeys.includes(item.Id);

              return (
                <div
                  key={item.Id}
                  onClick={() => onValueChange(item.Id)}
                  className={`flex items-center justify-between rounded cursor-pointer transition mb-1 ${
                    isSelected ? "bg-blue-50" : "hover:bg-blue-100"
                  }`}
                >
                  {/* content */}
                  <div
                    className={`h-full w-full text-sm p-2`}
                    style={{
                      ...(item.style ?? {}),
                      backgroundColor: item.BgColorHex ?? undefined,
                      color: item.TextColorHex ?? "#575757",
                    }}
                  >
                    <KeyValueMenuItem item={item} variant="chip" />
                  </div>

                  {/* delete */}
                  {itemsDeletable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-400 hover:text-red-500"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (onDeleteClick) {
                          await onDeleteClick(item.Id);
                        }
                      }}
                    >
                      <Trash size={12} />
                    </Button>
                  )}
                </div>
              );
            })}

            {/* EMPTY STATE FIX */}
            {filteredItems.length === 0 && (
              <div className="text-center text-gray-400 py-4 text-sm">
                No results found
              </div>
            )}
          </div>
        )}

        {/* EDIT */}
        {items && items.length > 0 && itemsEditable && (
          <div
            key={"edit"}
            className="flex items-center justify-center rounded text-sm cursor-pointer mt-2 p-2 text-gray-600 hover:bg-gray-200"
            onClick={() => onEditClick && onEditClick()}
          >
            <Pencil size={15} />
            <div className="ml-2">{editText ?? "Edit Labels"}</div>
          </div>
        )}
      </div>

      {/* CUSTOM ACTION */}
      {customActionTriggerRenderer && (
        <>
          <div className="h-px bg-gray-200" />
          <div className="m-1 cursor-pointer hover:bg-gray-200 rounded">
            {customActionTriggerRenderer(
              () => onCustomActionClick && onCustomActionClick(),
            )}
          </div>
        </>
      )}
    </>
  );
}
